import {
  ACCOUNT_TYPE,
  AdminRouteMap,
  ChallengeRouteMap,
  ExerciseRouteMap,
  PatientRouteMap,
  ResourceRouteMap,
  UsersRouteMap,
} from '_constants';
import { LoggedInViewWrapper, UserDetail } from 'components/templates';
import React, { ReactNode } from 'react';
import { Navigate, Routes, Route, Outlet } from 'react-router-dom';
import { usePermission } from 'utils/hooks/ctx-hooks';
import {
  ArchivedPatients,
  AddChallengeView,
  AddPatientView,
  ChallengeDetailView,
  ChallengeReportView,
  ChallengesView,
  EditChallengeView,
  EditExerciseView,
  EditPatientView,
  EditResourceView,
  ExerciseDetailView,
  ExerciseFormView,
  ExerciseReportView,
  ExercisesView,
  GoalReportView,
  HospitalsAndClinics,
  LibraryView,
  MessageView,
  Patient,
  PatientsView,
  Profile,
  ResourceDetailView,
  ResourceFormView,
  ResourcesView,
  FindPatientsView,
  UserManagement,
  ArchivedOrganizations,
  Organizations,
  OrganizationDetailView,
} from 'views';
import { ArchiveHospitalsAndClinics } from 'views/ArchiveHospitalsAndClinics';
import { EncounterReportView } from 'views/EncounterReportView';

const ProtectedRoute = ({
  children,
  condition,
  redirectTo,
}: {
  children?: ReactNode;
  condition: boolean;
  redirectTo: string;
}) => {
  if (condition) {
    return children ? <>{children}</> : <Outlet />;
  }
  return <Navigate to={redirectTo} />;
};

const PatientRotues = () => {
  const { permission } = usePermission();
  return (
    <Routes>
      <Route path={PatientRouteMap.ENCOUNTER_REPORT} element={<EncounterReportView />} />
      <Route path={PatientRouteMap.ADD} element={<AddPatientView />} />
      <Route
        element={
          <ProtectedRoute
            condition={
              permission === ACCOUNT_TYPE.ADMIN ||
              permission === ACCOUNT_TYPE.ORG_ADMIN ||
              permission === ACCOUNT_TYPE.HOS_ADMIN
            }
            redirectTo={PatientRouteMap.TABLE}
          />
        }
      >
        <Route path={PatientRouteMap.ARCHIVED} element={<ArchivedPatients />} />
      </Route>
      <Route path={PatientRouteMap.EDIT} element={<EditPatientView />} />
      <Route path={PatientRouteMap.REPORT} element={<GoalReportView />} />
      <Route path={PatientRouteMap.TABLE} element={<PatientsView />} />
      <Route path={PatientRouteMap.DETAIL} element={<Patient />} />
      <Route
        element={
          <ProtectedRoute
            condition={permission === ACCOUNT_TYPE.THERAPIST}
            redirectTo={PatientRouteMap.TABLE}
          />
        }
      >
        <Route path={PatientRouteMap.SEARCH} element={<FindPatientsView />} />
      </Route>
      <Route
        path={PatientRouteMap.ANY}
        element={<Navigate to={PatientRouteMap.TABLE} />}
      />
    </Routes>
  );
};

const ChallengeRotues = () => (
  <Routes>
    <Route path={ChallengeRouteMap.ADDFORM} element={<AddChallengeView />} />
    <Route path={ChallengeRouteMap.DETAIL} element={<ChallengeDetailView />} />
    <Route path={ChallengeRouteMap.EDIT} element={<EditChallengeView />} />
    <Route path={ChallengeRouteMap.REPORT} element={<ChallengeReportView />} />
    <Route path={ChallengeRouteMap.TABLEVIEW} element={<ChallengesView />} />
    <Route
      path={ChallengeRouteMap.ANY}
      element={<Navigate to={ChallengeRouteMap.TABLEVIEW} />}
    />
  </Routes>
);

const ExerciseRoutes = () => (
  <Routes>
    <Route path={ExerciseRouteMap.ADDFORM} element={<ExerciseFormView />} />
    <Route path={ExerciseRouteMap.DETAIL} element={<ExerciseDetailView />} />
    <Route path={ExerciseRouteMap.EDIT} element={<EditExerciseView />} />
    <Route path={ExerciseRouteMap.REPORT} element={<ExerciseReportView />} />
    <Route path={ExerciseRouteMap.TABLEVIEW} element={<ExercisesView />} />
    <Route
      path={ExerciseRouteMap.ANY}
      element={<Navigate to={ExerciseRouteMap.TABLEVIEW} />}
    />
  </Routes>
);

const ResourceRoutes = () => (
  <Routes>
    <Route path={ResourceRouteMap.ADDFORM} element={<ResourceFormView />} />
    <Route path={ResourceRouteMap.DETAIL} element={<ResourceDetailView />} />
    <Route path={ResourceRouteMap.EDIT} element={<EditResourceView />} />
    <Route path={ResourceRouteMap.TABLEVIEW} element={<ResourcesView />} />
    <Route
      path={ResourceRouteMap.ANY}
      element={<Navigate to={ResourceRouteMap.TABLEVIEW} />}
    />
  </Routes>
);

const UserRoutes = () => (
  <Routes>
    <Route path={UsersRouteMap.DETAIL} element={<UserDetail />} />
    <Route path={UsersRouteMap.EDIT} element={<UserDetail />} />
    <Route path={UsersRouteMap.TABLE} element={<UserManagement />} />
    <Route path={UsersRouteMap.ANY} element={<Navigate to={UsersRouteMap.TABLE} />} />
  </Routes>
);

export const AdminRoutes = () => {
  const { permission } = usePermission();
  return (
    <Routes>
      <Route path={AdminRouteMap.BLANK} element={<LoggedInViewWrapper />}>
        <Route path={AdminRouteMap.PATIENTS} element={<PatientRotues />} />
        <Route path={AdminRouteMap.LIBRARY} element={<LibraryView />} />
        <Route
          element={
            <ProtectedRoute
              condition={permission === ACCOUNT_TYPE.THERAPIST}
              redirectTo={AdminRouteMap.LIBRARY}
            />
          }
        >
          <Route path={AdminRouteMap.MESSAGE} element={<MessageView />} />
        </Route>
        <Route path={AdminRouteMap.EXERCISES} element={<ExerciseRoutes />} />
        <Route path={AdminRouteMap.CHALLENGES} element={<ChallengeRotues />} />
        <Route path={AdminRouteMap.RESOURCES} element={<ResourceRoutes />} />
        <Route path={AdminRouteMap.PROFILE} element={<Profile />} />
        <Route
          element={
            <ProtectedRoute
              condition={permission !== ACCOUNT_TYPE.THERAPIST}
              redirectTo={AdminRouteMap.LIBRARY}
            />
          }
        >
          <Route path={AdminRouteMap.USERS} element={<UserRoutes />} />
        </Route>
        <Route
          element={
            <ProtectedRoute
              condition={permission === ACCOUNT_TYPE.ADMIN}
              redirectTo={AdminRouteMap.USERS}
            />
          }
        >
          <Route path={AdminRouteMap.ORGANIZATIONS} element={<Organizations />} />
        </Route>
        <Route
          element={
            <ProtectedRoute
              condition={permission === ACCOUNT_TYPE.ADMIN}
              redirectTo={AdminRouteMap.USERS}
            />
          }
        >
          <Route
            path={AdminRouteMap.ARCHIVE_ORGANIZATIONS}
            element={<ArchivedOrganizations />}
          />
          <Route
            path={AdminRouteMap.ARCHIVE_ORGANIZATIONS_DETAIL}
            element={<OrganizationDetailView />}
          />
        </Route>
        <Route
          element={
            <ProtectedRoute
              condition={
                permission === ACCOUNT_TYPE.ADMIN || permission === ACCOUNT_TYPE.ORG_ADMIN
              }
              redirectTo={AdminRouteMap.USERS}
            />
          }
        >
          <Route path={AdminRouteMap.HOSPITALS} element={<HospitalsAndClinics />} />
        </Route>
        <Route
          element={
            <ProtectedRoute
              condition={permission === ACCOUNT_TYPE.ADMIN}
              redirectTo={AdminRouteMap.USERS}
            />
          }
        >
          <Route
            path={AdminRouteMap.ARCHIVE_HOSPITALS}
            element={<ArchiveHospitalsAndClinics />}
          />
        </Route>
        <Route
          path={AdminRouteMap.ANY}
          element={<Navigate to={AdminRouteMap.LIBRARY} />}
        />
      </Route>
    </Routes>
  );
};
